// Explicit stereo channel routing.
//
// Why this exists: the naive approach — connect a source straight to
// `audioContext.destination` — relies on the browser's *implicit* channel
// interpretation, which is allowed to upmix/downmix depending on
// `channelCount`/`channelCountMode`/`channelInterpretation` on intermediate
// nodes. That implicit behavior is exactly what this app cannot risk: if the
// click track ever leaks into the left/PA channel, or backing tracks ever
// bleed into the click channel, that is heard live by an audience.
//
// Instead every playable signal is routed through an explicit
// ChannelSplitterNode(2) -> ChannelMergerNode(2) pair, wired one input to one
// output with no shared node in between. This makes the routing an explicit,
// inspectable graph rather than an implicit browser default, and it is what
// lets buildRightOnlyClickBuffer's silence guarantee below be verified by a
// unit test that reads the actual output samples.

import { generateClickSamples, type ClickSound } from './clickSounds';

export interface StereoRoute {
  splitter: ChannelSplitterNode;
  merger: ChannelMergerNode;
  /** Connect an upstream node's output to this route's input (the splitter). */
  input: AudioNode;
  /** The final node in the route; connect this to the destination (or a GainNode first). */
  output: AudioNode;
}

/**
 * Builds a splitter -> merger pair with explicit 1:1 channel wiring:
 * splitter output 0 (left) -> merger input 0 (left)
 * splitter output 1 (right) -> merger input 1 (right)
 * No node here ever combines the two channels.
 */
export function createExplicitStereoPassthrough(context: BaseAudioContext): StereoRoute {
  const splitter = context.createChannelSplitter(2);
  const merger = context.createChannelMerger(2);
  splitter.connect(merger, 0, 0);
  splitter.connect(merger, 1, 1);
  return { splitter, merger, input: splitter, output: merger };
}

/**
 * Builds a route that forces ALL input audio to the LEFT output only,
 * used for "Tracks Only" mode when the user opts into left-only routing
 * for mono/compatibility reasons. The right channel is a merger input that
 * is simply never connected to, which the Web Audio spec defines as silence
 * (constant zero), not as a hidden mix of the left signal.
 */
export function createLeftOnlyRoute(context: BaseAudioContext): StereoRoute {
  const splitter = context.createChannelSplitter(2);
  const merger = context.createChannelMerger(2);
  splitter.connect(merger, 0, 0); // left -> left
  splitter.connect(merger, 1, 0); // right source content ALSO folded to left, never to output right
  return { splitter, merger, input: splitter, output: merger };
}

/**
 * Mirror of createLeftOnlyRoute: forces ALL input audio to the RIGHT output
 * only. Used wherever a source's own two channels need to be summed onto
 * just the right side — e.g. a Tracks + Click song's own file audio, so the
 * synthetic click can have the left side to itself instead.
 */
export function createRightOnlyRoute(context: BaseAudioContext): StereoRoute {
  const splitter = context.createChannelSplitter(2);
  const merger = context.createChannelMerger(2);
  splitter.connect(merger, 0, 1); // left source content folded to right, never to output left
  splitter.connect(merger, 1, 1); // right -> right
  return { splitter, merger, input: splitter, output: merger };
}

/**
 * Generates a stereo click buffer for the metronome where channel 0 (left)
 * is guaranteed to be all zeros and channel 1 (right) carries the click.
 * This is the buffer whose raw samples the unit test inspects directly.
 */
export function buildStereoClickBuffer(
  context: BaseAudioContext,
  options: { frequency: number; durationSeconds: number; sampleRate: number; gain: number },
): AudioBuffer {
  const { frequency, durationSeconds, sampleRate, gain } = options;
  const frameCount = Math.max(1, Math.round(durationSeconds * sampleRate));
  const buffer = context.createBuffer(2, frameCount, sampleRate);

  const left = buffer.getChannelData(0);
  const right = buffer.getChannelData(1);
  left.fill(0); // explicit silence guarantee, not just "never written to"

  for (let i = 0; i < frameCount; i++) {
    const t = i / sampleRate;
    // Short decaying sine "tick" so it reads clearly as a click, not a tone.
    const envelope = Math.exp(-t * 40);
    right[i] = Math.sin(2 * Math.PI * frequency * t) * envelope * gain;
  }

  return buffer;
}

/**
 * Generates a stereo click buffer using a selectable synthesized timbre
 * (see clickSounds.ts), placed onto the requested output side. 'right'
 * (the historical default) guarantees channel 0 (left) is all zeros; 'left'
 * is the mirror; 'stereo' writes the same click to both channels. This is
 * the single place mono click samples ever get placed into a stereo buffer,
 * which is what keeps the silence guarantee centralized and testable
 * regardless of which sound/side is selected.
 */
export function buildSelectableStereoClickBuffer(
  context: BaseAudioContext,
  options: { sound: ClickSound; isAccent: boolean; isSubdivision: boolean; sampleRate: number; gain: number; channel?: 'stereo' | 'left' | 'right' },
): AudioBuffer {
  const { samples, durationSeconds } = generateClickSamples(options);
  const frameCount = Math.max(1, Math.round(durationSeconds * options.sampleRate));
  const buffer = context.createBuffer(2, frameCount, options.sampleRate);
  const channel = options.channel ?? 'right';

  const left = buffer.getChannelData(0);
  const right = buffer.getChannelData(1);
  const clickSamples = samples.subarray(0, frameCount);

  if (channel === 'left') {
    left.set(clickSamples);
    right.fill(0);
  } else if (channel === 'stereo') {
    left.set(clickSamples);
    right.set(clickSamples);
  } else {
    left.fill(0);
    right.set(clickSamples);
  }

  return buffer;
}
